/**
 * EmbeddedOnboardingGate
 *
 * One-time personalization survey for the Bitrix24 embedded app. A fresh install
 * seeds a *generic* demo dataset immediately (so the dashboard is never empty),
 * then this gate asks the org owner for industry/role/team-size and re-seeds the
 * demo to match. It renders inside AccessRequestGate, wrapping the dashboard.
 *
 * Shows ONLY when:
 *   - the embedded session belongs to the org owner (`role === 'client'`), AND
 *   - the backend reports `onboardingSurveyCompleted === false` (fresh install
 *     still on the generic seed).
 * Everyone else (agents, already-personalized orgs) passes straight through.
 *
 * On submit it POSTs the survey to the session-authed embedded endpoint, then
 * `refreshSession()` — the backend flips the flag, so the gate re-renders the
 * dashboard.
 */
import { useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { useEmbeddedOrg } from '@/contexts/EmbeddedOrgContext';
import { useEmbeddedAuthFetch } from '@/hooks/useEmbeddedAuthFetch';
import { embeddedGateHeightClass } from '@/lib/embedded-host';
import { API_ENDPOINTS } from '@/lib/api/endpoints';
import { useTranslation } from 'react-i18next';
import {
  QuickContextStep,
  type QuickContextValues,
} from '@/features/onboarding/steps/QuickContextStep';

interface EmbeddedOnboardingGateProps {
  children: ReactNode;
}

const SUPPORTED_LOCALES = ['en', 'ru', 'uk', 'es'] as const;
type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

function resolveLocale(lang: string): SupportedLocale {
  const short = lang.slice(0, 2).toLowerCase();
  return (SUPPORTED_LOCALES as readonly string[]).includes(short)
    ? (short as SupportedLocale)
    : 'en';
}

export function EmbeddedOnboardingGate({ children }: EmbeddedOnboardingGateProps) {
  const { i18n, t } = useTranslation('onboarding');
  const { role, onboardingSurveyCompleted, isLoading, refreshSession, crmVendor } =
    useEmbeddedOrg();
  const { authenticatedFetch } = useEmbeddedAuthFetch();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Still resolving the session → let AccessRequestGate's spinner cover it;
  // rendering children here would flash the dashboard before the survey.
  if (isLoading) {
    return (
      <div
        className={`flex ${embeddedGateHeightClass(crmVendor, 'h-screen')} items-center justify-center bg-bg`}
        role="status"
      >
        <Loader2 className="h-8 w-8 animate-spin text-fg-muted" />
      </div>
    );
  }

  // Only the org owner on a fresh (generic) install sees the survey.
  if (role !== 'client' || onboardingSurveyCompleted !== false) {
    return <>{children}</>;
  }

  const handleSubmit = async (values: QuickContextValues) => {
    try {
      setSubmitting(true);
      setError(null);

      // authenticatedFetch rewrites /api/v1/* → /api/v1/embedded/* and attaches
      // the _org routing hint + CSRF token. Authentication itself stays in the
      // HttpOnly cookie and is never copied into a request URL.
      await authenticatedFetch(API_ENDPOINTS.DEMO_DATA_ONBOARD, {
        method: 'POST',
        body: JSON.stringify({
          industry: values.industry,
          role: values.role,
          teamSize: values.teamSize,
          locale: resolveLocale(i18n.language),
          // Шаг опроса спрашивает название компании — и оно должно доехать до
          // организации. Установка из Bitrix24 называет её `<домен> (Bitrix24)`,
          // и без этого поля клиент видел домен вместо введённого имени (#360).
          organizationName: values.organizationName,
        }),
      });

      // Backend set onboardingSurveyCompleted=true + re-seeded the demo; re-read
      // the session so the flag flips and the dashboard renders.
      await refreshSession();
    } catch {
      setError(t('saas.quickContext.submitError'));
      setSubmitting(false);
    }
  };

  return (
    <div
      className={`${embeddedGateHeightClass(crmVendor, 'min-h-screen')} overflow-y-auto bg-bg px-4 py-10`}
    >
      <div className="mx-auto w-full max-w-3xl">
        <QuickContextStep onNext={handleSubmit} isSubmitting={submitting} error={error} />
      </div>
    </div>
  );
}
